import { BlockStack, Card, FormLayout, Select, Text } from '@shopify/polaris'
import { useFormikContext } from 'formik'

import {
  InternalLink,
  PoliciesLink,
  useFormikHandleChangeCallback,
  useShopPermission,
} from '../common/helpers'
import { HelpLabel } from '../common/HelpIcon'
import { UpgradeBanner } from '../common/UpgradeBanner'
import * as urls from '../common/urls'
import { UserSettings } from './api'

export const DeliveryAddressAutoUpdateCard = () => {
  const formik = useFormikContext<UserSettings>()
  const handleChange = useFormikHandleChangeCallback(formik.setFieldValue)
  const subscriptionDeliveryAddressAutoUpdateAllowed = useShopPermission(
    'subscriptionDeliveryAddressAutoUpdateAllowed'
  )

  if (!subscriptionDeliveryAddressAutoUpdateAllowed) {
    return (
      <Card>
        <UpgradeBanner>
          Please{' '}
          <InternalLink url={urls.pricingPlansUrl()} target={'_blank'}>
            upgrade
          </InternalLink>{' '}
          to our <Text as="strong">Pro Plan</Text> or higher to access automatic subscription
          delivery address updates.
        </UpgradeBanner>
      </Card>
    )
  }

  return (
    <Card>
      <FormLayout>
        <BlockStack gap="400">
          <Select
            id="subscription_delivery_address_auto_update"
            name="subscription_delivery_address_auto_update"
            label={
              <HelpLabel
                tooltip="PayWhirl keeps track of customer addresses in Shopify. Enable this feature to
                  automatically update the subscription’s delivery address and method, when a
                  customer edits the corresponding address in their Shopify account."
              >
                Delivery address auto-update
              </HelpLabel>
            }
            options={[
              { label: 'Disabled', value: 'disabled' },
              {
                label: 'Update delivery address and method, but keep price',
                value: 'enabled-without-price',
              },
              {
                label: 'Update delivery address, method and recalculate price',
                value: 'enabled-with-price',
              },
            ]}
            value={formik.values.subscription_delivery_address_auto_update}
            onChange={handleChange}
          />
          {formik.values.subscription_delivery_address_auto_update !== 'disabled' && (
            <Text as="span" tone="subdued">
              Note: Subscription delivery address will be updated only if there is an available
              delivery option for the new address.
            </Text>
          )}
          {formik.values.subscription_delivery_address_auto_update === 'enabled-with-price' && (
            <Text as="span" tone="subdued">
              Note: Delivery price will be automatically updated based on the new delivery method.
              This may affect the subscription's total cost. Make sure to review and update your{' '}
              <PoliciesLink>store policies</PoliciesLink> to include information about address
              updates and delivery price recalculation.
            </Text>
          )}
        </BlockStack>
      </FormLayout>
    </Card>
  )
}
